import React, { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { useAuth } from './authentication'
import { getVideoToken } from '../api/endpoints'

type WebsocketMessageHandler = (message: MessageEvent) => void

interface WebsocketContextValue {
  webSocket: WebSocket
  setIsWebsocketLoginReady: (ready: boolean) => void
  registerWebsocketMessaging: (handlerFunction: WebsocketMessageHandler) => () => void
  websocketOpen: boolean
}

const WebsocketContext = createContext<WebsocketContextValue | undefined>(undefined)

// Mirrors react/src/api/client.ts's BASE_URL: defaults to the page's own
// origin (the backend serves API, WebSocket, and the React build from one
// host:port - see main.ts), overridable via REACT_APP_API_BASE_URL for
// setups where the frontend talks to a different origin.
const getWebsocketUrl = (): string => {
  const base = process.env.REACT_APP_API_BASE_URL ?? '.'
  const { protocol, host } = base === '.' ? window.location : new URL(base, window.location.href)
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'
  return wsProtocol + '//' + host
}

export const WebsocketProvider = ({ children }: { children: ReactNode }) => {
  const { accessToken } = useAuth()
  const websocketMessageHandler = useRef<WebsocketMessageHandler[]>([])
  const [isWebsocketLoginReady, setIsWebsocketLoginReady] = useState(false)
  const [websocketOpen, setWebsocketOpen] = useState(false)

  const webSocket = useMemo(() => new WebSocket(getWebsocketUrl()), [])
  webSocket.binaryType = 'arraybuffer'
  webSocket.onopen = () => {
    setWebsocketOpen(true)
  }

  webSocket.onclose = () => {
    setWebsocketOpen(false)
  }

  webSocket.onmessage = message => {
    for (const messageHandler of websocketMessageHandler.current) {
      messageHandler(message)
    }
  }

  useEffect(() => {
    if (isWebsocketLoginReady && websocketOpen) {
      getVideoToken().then(({ ok, data }) => {
        if (ok && data.videoToken) {
          webSocket.send('token=' + data.videoToken)
        }
      })
    }
  }, [accessToken, isWebsocketLoginReady, webSocket, websocketOpen])

  const registerWebsocketMessaging = (handlerFunction: WebsocketMessageHandler) => {
    websocketMessageHandler.current.push(handlerFunction)
    return () => {
      websocketMessageHandler.current = websocketMessageHandler.current.filter(
        handler => handler !== handlerFunction
      )
    }
  }

  return (
    <WebsocketContext
      value={{ webSocket, setIsWebsocketLoginReady, registerWebsocketMessaging, websocketOpen }}
    >
      {children}
    </WebsocketContext>
  )
}

export const useWebsocket = (): WebsocketContextValue => {
  const context = useContext(WebsocketContext)
  if (!context) {
    throw new Error('useWebsocket must be used within an WebsocketProvider')
  }
  return context
}
